export type TaskUrgencyLike = {
  id?: string | number | null;
  projectId?: string | number | null;
  dueDate?: string | null;
  priority?: string | null;
  status?: string | null;
  urgencyScore?: string | number | null;
  category?: string | null;
};

const MY_WORK_FINANCE_EXCLUDED_EMAILS = new Set([
  'yl@linfinityeng.com',
]);

function normalize(value: unknown): string {
  return String(value || '').trim().toLowerCase();
}
function dateOnlyTime(value?: string | null): number | null {
  const normalized = String(value || '').trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(normalized)) return null;
  const timestamp = new Date(`${normalized}T00:00:00`).getTime();
  return Number.isFinite(timestamp) ? timestamp : null;
}

export function calculateTaskUrgencyScore(task: TaskUrgencyLike, now: Date = new Date()): number {
  const suppliedScore = Number(task.urgencyScore);
  if (Number.isFinite(suppliedScore) && task.urgencyScore !== null && task.urgencyScore !== '') {
    return suppliedScore;
  }

  const status = normalize(task.status);
  if (['completed', 'complete', 'done'].includes(status)) return -10000;

  const priorityScore = {
    critical: 300,
    high: 180,
    medium: 60,
    low: 0,
  }[normalize(task.priority)] ?? 0;
  const statusScore = status === 'overdue' ? 180 : ['in progress', 'progress', 'ongoing'].includes(status) ? 40 : 0;
  const dueTime = dateOnlyTime(task.dueDate);
  let dueScore = 80;

  if (dueTime !== null) {
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const days = Math.round((dueTime - today) / 86_400_000);
    if (days < 0) dueScore = 1000 + Math.min(-days, 30) * 20;
    else if (days === 0) dueScore = 900;
    else if (days <= 3) dueScore = 760 - days * 20;
    else if (days <= 7) dueScore = 600 - days * 10;
    else if (days <= 14) dueScore = 400 - days * 5;
    else dueScore = 120;
  }

  return dueScore + priorityScore + statusScore;
}

export function compareTaskUrgency(left: TaskUrgencyLike, right: TaskUrgencyLike): number {
  const scoreDifference = calculateTaskUrgencyScore(right) - calculateTaskUrgencyScore(left);
  if (scoreDifference !== 0) return scoreDifference;

  const leftDue = dateOnlyTime(left.dueDate) ?? Number.MAX_SAFE_INTEGER;
  const rightDue = dateOnlyTime(right.dueDate) ?? Number.MAX_SAFE_INTEGER;
  if (leftDue !== rightDue) return leftDue - rightDue;

  return String(right.id || '').localeCompare(String(left.id || ''), 'en-AU', {
    numeric: true,
    sensitivity: 'base',
  });
}

export function selectDiversifiedTasks<T extends TaskUrgencyLike>(
  tasks: T[],
  limit: number,
  maxPerProject: number = 2
): T[] {
  const selected: T[] = [];
  const projectCounts = new Map<string, number>();

  for (const task of [...tasks].sort(compareTaskUrgency)) {
    if (selected.length >= limit) break;
    const projectKey = task.projectId === null || task.projectId === undefined || task.projectId === ''
      ? `unlinked:${String(task.id || selected.length)}`
      : `project:${String(task.projectId)}`;
    const currentCount = projectCounts.get(projectKey) || 0;
    if (currentCount >= maxPerProject) continue;
    projectCounts.set(projectKey, currentCount + 1);
    selected.push(task);
  }

  return selected;
}

export function buildDiversifiedTaskBatches<T extends TaskUrgencyLike>(
  tasks: T[],
  batchSize: number,
  maxPerProject: number = 2
): T[][] {
  if (!Number.isFinite(batchSize) || batchSize <= 0 || !tasks.length) return [];

  let remaining = [...tasks].sort(compareTaskUrgency);
  const batches: T[][] = [];

  while (remaining.length) {
    const batch = selectDiversifiedTasks(remaining, Math.trunc(batchSize), maxPerProject);
    if (!batch.length) break;
    const selected = new Set(batch);
    batches.push(batch);
    remaining = remaining.filter((task) => !selected.has(task));
  }

  return batches;
}

export function filterMyWorkTasksForUser<T extends TaskUrgencyLike>(tasks: T[], userEmail?: string | null): T[] {
  if (!MY_WORK_FINANCE_EXCLUDED_EMAILS.has(normalize(userEmail))) return tasks;
  return tasks.filter((task) => normalize(task.category) !== 'finance');
}

export function getNextMyWorkBatchIndex(currentIndex: number, batchCount: number): number {
  if (!Number.isFinite(batchCount) || batchCount <= 1) return 0;
  const normalizedIndex = Number.isFinite(currentIndex)
    ? Math.max(0, Math.trunc(currentIndex)) % Math.trunc(batchCount)
    : 0;
  return (normalizedIndex + 1) % Math.trunc(batchCount);
}
